import { trpc } from "@api/client";
import { useSelectedCompany } from "@flash-tecnologia/hros-web-utility";
import { useCreateDepartment } from "@hooks/use-create-department";
import dispatchToast from "@utils/dispatch-toast";
import { useState } from "react";
import { REQUIRED_FORM_FIELD_ERROR } from "../constants";
import { useSetupSummary } from "../context/setup-summary-context";
import { Department } from "../types";

export function useCreateDepartmentDrawer({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: (department: Department) => void;
}) {
  const utils = trpc.useUtils();
  const { incrementDepartmentsCreated } = useSetupSummary();

  const { selectedCompany } = useSelectedCompany();

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [nameError, setNameError] = useState<string>();

  const [isCreateDepartmentLoading, setIsCreateDepartmentLoading] =
    useState(false);

  const { createDepartment } = useCreateDepartment({});

  const handleDrawerClose = () => {
    onClose();
    resetForm();
  };

  const handleNameBlur = () => {
    if (!name) {
      setNameError(REQUIRED_FORM_FIELD_ERROR);
    } else {
      setNameError(undefined);
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setNameError(undefined);
  };

  const handleCreateDepartment = async () => {
    setIsCreateDepartmentLoading(true);
    try {
      if (!name) {
        setNameError(REQUIRED_FORM_FIELD_ERROR);
        setIsCreateDepartmentLoading(false);
        return;
      }

      const newDepartment = await createDepartment({
        name,
        description,
        companyId: selectedCompany.id,
      });

      const queryInput = { companyId: selectedCompany.id };

      const newDepartments = await utils.client.getDepartments.query(
        queryInput
      );

      utils.getDepartments.setData(queryInput, newDepartments);

      onSuccess(newDepartment);

      dispatchToast({
        content: "Departamento criado com sucesso!",
        type: "success",
      });

      incrementDepartmentsCreated();

      setIsCreateDepartmentLoading(false);

      handleDrawerClose();
    } catch (error) {
      setIsCreateDepartmentLoading(false);
    }
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setNameError(undefined);
  };

  return {
    name,
    description,
    nameError,
    isLoading: isCreateDepartmentLoading,

    onDrawerSubmit: handleCreateDepartment,
    onDescriptionChange: setDescription,
    onDrawerClose: handleDrawerClose,
    onNameBlur: handleNameBlur,
    onNameChange: handleNameChange,
  };
}
